import React, {
  forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState,
} from "react";
import { Modal, Button, Spinner, Form, Badge, Alert } from "react-bootstrap";
import { FiBox, FiLayout, FiCheckCircle, FiLock } from "react-icons/fi";
import api, { BASE_URL } from "../../Utils/api";
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { podeSelecionarPlano } from "../../Utils/validarTrocaPlano";
import Precos from "../Precos/Precos";
import EmpresasFinanceiroModal from "./EmpresasFinanceiroModal";
import "./EmpresasConfiguracoesModal.css";

// Extraído de EmpresasConfiguracoesModal.js (aba "Plano") para ser reutilizado
// tanto lá quanto embutido na etapa "Escolha o Plano" do assistente de Auto
// Configuração — mesma lógica de resumo/uso/limites/forma de pagamento, sem
// duplicar código. Ver useImperativeHandle para a API usada pelo modal (que
// só aplica o plano pendente quando o usuário clica no Salvar geral).

const OPCOES_BILLING_TYPE = [
  { value: "PIX", label: "PIX" },
  { value: "BOLETO", label: "Boleto bancário" },
  { value: "CREDIT_CARD", label: "Cartão de crédito" },
  { value: "UNDEFINED", label: "Cliente escolhe na fatura" },
];

function labelBillingType(value) {
  const norm = String(value || "").toUpperCase();
  const opcao = OPCOES_BILLING_TYPE.find((o) => o.value === norm);
  if (opcao) return opcao.label;
  if (norm === "GRATUITO" || !norm) return "—";
  return value;
}

function planoPagoPendente(plano) {
  return Boolean(plano && plano.codigo !== "gratuito");
}

function varianteStatusPlano(status) {
  const normalizado = String(status || "").toUpperCase();
  if (normalizado === "ATIVA" || normalizado === "ACTIVE") return "success";
  if (normalizado.includes("PENDENTE")) return "warning";
  return "secondary";
}

const SelecaoPlano = forwardRef(function SelecaoPlano(
  {
    idEntidade,
    empresaNome,
    standalone = false,
    onPlanoAplicado,
    mostrarConfirmacaoStandalone = true,
    onPlanoSelecionadoChange,
  },
  ref
) {
  const { toast } = useFeedback();
  const { setGlobalData } = useContext(GlobalContext);
  const abrirModuloEmNovaAba = useCallback((path, classificacao) => {
    if (classificacao) {
      localStorage.setItem("classificacao", classificacao);
      setGlobalData((prev) => ({ ...prev, classificacao }));
    }
    const url = `${window.location.origin}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }, [setGlobalData]);
  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      id_entidade: idEntidade,
    }),
    [idEntidade]
  );

  const [planoResumo, setPlanoResumo] = useState(null);
  const [planoLoading, setPlanoLoading] = useState(false);
  const [planoPendente, setPlanoPendente] = useState(null);
  const [billingTypePlano, setBillingTypePlano] = useState("");
  const [catalogoPlanos, setCatalogoPlanos] = useState([]);
  const [showPrecosModal, setShowPrecosModal] = useState(false);
  const [showFinanceiroModal, setShowFinanceiroModal] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [erroStandalone, setErroStandalone] = useState(null);
  const [sucessoStandalone, setSucessoStandalone] = useState(null);

  const fetchPlanoResumo = useCallback(async (opcoes = {}) => {
    if (!idEntidade) return;
    const silencioso = opcoes?.silencioso === true;
    try {
      if (!silencioso) setPlanoLoading(true);
      const { data } = await api.get(`${BASE_URL}/api/assinaturas/resumo`, {
        headers: authHeaders,
      });
      setPlanoResumo(data?.data || null);
    } catch {
      if (!silencioso) setPlanoResumo(null);
    } finally {
      if (!silencioso) setPlanoLoading(false);
    }
  }, [idEntidade, authHeaders]);

  const fetchCatalogoPlanos = useCallback(async () => {
    try {
      const { data } = await api.get(`${BASE_URL}/api/tabelasPrecos/vigente`);
      setCatalogoPlanos(data?.data?.planos || []);
    } catch {
      setCatalogoPlanos([]);
    }
  }, []);

  const possuiAssinaturaPendente = Boolean(planoResumo?.assinatura_pendente);

  useEffect(() => {
    if (!possuiAssinaturaPendente) return;
    setPlanoPendente(null);
    setBillingTypePlano("");
    setShowPrecosModal(false);
  }, [possuiAssinaturaPendente]);

  useEffect(() => {
    if (onPlanoSelecionadoChange) {
      onPlanoSelecionadoChange(Boolean(planoPendente));
    }
  }, [onPlanoSelecionadoChange, planoPendente]);

  useEffect(() => {
    if (!idEntidade) return undefined;

    const atualizarAoRetornar = () => fetchPlanoResumo({ silencioso: true });
    window.addEventListener("focus", atualizarAoRetornar);
    const intervalo = possuiAssinaturaPendente
      ? window.setInterval(atualizarAoRetornar, 5000)
      : null;

    return () => {
      window.removeEventListener("focus", atualizarAoRetornar);
      if (intervalo) window.clearInterval(intervalo);
    };
  }, [idEntidade, possuiAssinaturaPendente, fetchPlanoResumo]);

  useEffect(() => {
    if (!idEntidade) return;
    fetchPlanoResumo();
    fetchCatalogoPlanos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEntidade]);

  const handleSelecionarPlano = (plano) => {
    const compativel = podeSelecionarPlano({
      plano,
      uso: planoResumo?.uso,
      catalogoPlanos,
      toast,
    });
    if (!compativel) return;

    setPlanoPendente(plano);
    setBillingTypePlano("");
    setShowPrecosModal(false);
    setErroStandalone(null);
    setSucessoStandalone(null);
  };

  const aplicarPlanoPendente = async () => {
    if (!planoPendente || !idEntidade) return null;

    if (
      !podeSelecionarPlano({
        plano: planoPendente,
        uso: planoResumo?.uso,
        catalogoPlanos,
        toast,
      })
    ) {
      return null;
    }

    const ciclo =
      planoPendente.ciclo === "GRATUITO" || planoPendente.codigo === "gratuito"
        ? "GRATUITO"
        : planoPendente.ciclo;

    const body = {
      codigo_plano: planoPendente.codigo,
      ciclo: ciclo === "GRATUITO" ? "MENSAL" : ciclo,
      confirmar_remocao_recorrencia: true,
    };
    if (planoPagoPendente(planoPendente)) {
      body.billing_type = billingTypePlano;
    }

    const { data } = await api.post(
      `${BASE_URL}/api/assinaturas/alterar-plano`,
      body,
      { headers: authHeaders }
    );

    const resultado = data?.data;
    if (resultado?.resumo) setPlanoResumo(resultado.resumo);
    if (resultado?.requer_checkout && resultado?.checkout_url) {
      window.open(resultado.checkout_url, "_blank", "noopener,noreferrer");
      if (!resultado?.resumo) await fetchPlanoResumo();
      setPlanoPendente(null);
      setBillingTypePlano("");
      return {
        tipo: "checkout",
        message: data?.message || "Conclua o pagamento no Asaas para ativar o plano.",
      };
    }

    if (resultado?.aguardando_pagamento) {
      if (!resultado?.resumo) await fetchPlanoResumo();
      setPlanoPendente(null);
      setBillingTypePlano("");
      return {
        tipo: "checkout",
        message: data?.message || "Aguardando confirmação do pagamento para ativar o plano.",
      };
    }

    if (!resultado?.resumo) await fetchPlanoResumo();
    setPlanoPendente(null);
    setBillingTypePlano("");
    return {
      tipo: "alterado",
      message: data?.message || "Plano alterado com sucesso.",
    };
  };

  const handleConfirmarStandalone = async () => {
    if (planoPagoPendente(planoPendente) && !billingTypePlano) {
      setErroStandalone("Selecione a forma de pagamento da assinatura antes de confirmar.");
      return;
    }
    if (possuiAssinaturaPendente) {
      setErroStandalone("Há um plano aguardando pagamento. Conclua a fatura antes de escolher outro.");
      return;
    }
    setAplicando(true);
    setErroStandalone(null);
    setSucessoStandalone(null);
    try {
      const resultado = await aplicarPlanoPendente();
      if (resultado === null) return;
      setSucessoStandalone(resultado.message);
      if (onPlanoAplicado) onPlanoAplicado(resultado);
    } catch (err) {
      const mensagem = err.response?.data?.message || "Erro ao confirmar o plano. Tente novamente.";
      if (err.response?.data?.code === "USO_INCOMPATIVEL_PLANO") toast.warning(mensagem);
      setErroStandalone(mensagem);
    } finally {
      setAplicando(false);
    }
  };

  useImperativeHandle(ref, () => ({
    temPlanoPendente: () => Boolean(planoPendente),
    precisaFormaPagamento: () => planoPagoPendente(planoPendente) && !billingTypePlano,
    temAssinaturaPendenteBloqueando: () => Boolean(planoPendente) && possuiAssinaturaPendente,
    aplicarPlanoPendente,
  }));

  const planoAtualCodigo = planoResumo?.assinatura?.plano_codigo || null;
  const planoExibidoNome =
    planoPendente?.nome || planoResumo?.assinatura?.plano_nome || "Ver planos";
  const botaoPlanoLabel = planoPendente ? `Plano ${planoPendente.nome}` : "Ver planos";

  const renderAlerta = (msg, tipo) => (
    <Alert variant={tipo} className="empresa-config-alerta">
      {tipo === "success" ? <FiCheckCircle aria-hidden="true" /> : <FiLock aria-hidden="true" />}
      <span>{msg}</span>
    </Alert>
  );

  const renderFormaPagamento = () => (
    <div className="empresa-config-card">
      <Form.Label className="empresa-config-label">Forma de pagamento da assinatura</Form.Label>
      <Form.Select
        size="sm"
        value={billingTypePlano}
        onChange={(e) => setBillingTypePlano(e.target.value)}
        disabled={aplicando}
      >
        <option value="">Selecione a forma de pagamento...</option>
        {OPCOES_BILLING_TYPE.map((opcao) => (
          <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
        ))}
      </Form.Select>
    </div>
  );

  if (planoLoading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spinner animation="border" size="sm" /> Carregando plano…
      </div>
    );
  }

  if (!planoResumo) {
    return renderAlerta("Não foi possível carregar o plano desta entidade.", "info");
  }

  return (
    <>
      {standalone && erroStandalone && renderAlerta(erroStandalone, "danger")}
      {standalone && sucessoStandalone && renderAlerta(sucessoStandalone, "success")}

      {!standalone && (
      <div className="empresa-config-card empresa-config-plano-atual">
        <div className="empresa-config-label">Plano atual</div>
        <div className="empresa-config-plano-nome">
          <FiBox aria-hidden="true" />
          <strong>{planoExibidoNome}</strong>
          <Badge bg={varianteStatusPlano(planoResumo.assinatura?.status)} pill>
            {planoResumo.assinatura?.status || "—"}
          </Badge>
        </div>
        {planoPendente && (
          <div className="empresa-config-hint empresa-config-hint--accent">
            Novo plano selecionado —
            {planoPagoPendente(planoPendente) ? " escolha a forma de pagamento e" : ""}{" "}
            {standalone ? (mostrarConfirmacaoStandalone ? "clique em Confirmar plano para aplicar" : "clique em Pr\u00f3xima para aplicar") : "clique em Salvar para aplicar"}
            {planoPendente.ciclo && planoPendente.ciclo !== "GRATUITO"
              ? ` (${planoPendente.ciclo === "ANUAL" ? "anual" : "mensal"})`
              : ""}
          </div>
        )}
        <div className="empresa-config-hint">
          Ciclo:{" "}
          {planoPendente?.ciclo && planoPendente.ciclo !== "GRATUITO"
            ? planoPendente.ciclo
            : planoResumo.assinatura?.ciclo_cobranca || "—"}{" "}
          · Status: {planoResumo.assinatura?.status || "—"}
          {!planoPendente && planoResumo.assinatura?.billing_type && (
            <> · Pagamento: {labelBillingType(planoResumo.assinatura.billing_type)}</>
          )}
        </div>
        {planoResumo.assinatura_pendente && (
          <div className="empresa-config-hint empresa-config-hint--accent">
            Aguardando pagamento: {planoResumo.assinatura_pendente.plano_nome}
            {planoResumo.assinatura_pendente.ciclo_cobranca
              ? ` (${planoResumo.assinatura_pendente.ciclo_cobranca.toLowerCase()})`
              : ""}
          </div>
        )}
      </div>
      )}

      {!standalone && planoPagoPendente(planoPendente) && renderFormaPagamento()}

      {!standalone && (
      <div className="empresa-config-card">
        <div className="empresa-config-card-title">
          <FiLayout aria-hidden="true" />
          <span>Uso versus limites</span>
        </div>
        <ul className="empresa-config-uso-lista">
          <li>
            <button
              type="button"
              className="empresa-config-link"
              onClick={() => abrirModuloEmNovaAba("/Documentos")}
            >
              Documentos
            </button>
            {`: ${planoResumo.uso?.documentosAtivos ?? 0}`}
            {planoResumo.limites?.find((l) => l.codigo === "DOCUMENTOS_ATIVOS")?.valor_numerico != null &&
              ` / ${planoResumo.limites.find((l) => l.codigo === "DOCUMENTOS_ATIVOS").valor_numerico}`}
          </li>
          <li>
            <button
              type="button"
              className="empresa-config-link"
              onClick={() => abrirModuloEmNovaAba("/Usuario")}
            >
              Usuários
            </button>
            {`: ${planoResumo.uso?.usuariosOperacionais ?? 0}`}
            {planoResumo.limites?.find((l) => l.codigo === "USUARIOS_OPERACIONAIS")?.valor_numerico != null &&
              ` / ${planoResumo.limites.find((l) => l.codigo === "USUARIOS_OPERACIONAIS").valor_numerico}`}
          </li>
          <li>
            <button
              type="button"
              className="empresa-config-link"
              onClick={() => abrirModuloEmNovaAba("/Empresas", "V")}
            >
              Filiais
            </button>
            {`: ${planoResumo.uso?.filiais ?? 0}`}
            {planoResumo.limites?.find((l) => l.codigo === "FILIAIS")?.valor_numerico != null &&
              ` / ${planoResumo.limites.find((l) => l.codigo === "FILIAIS").valor_numerico}`}
            <span className="empresa-config-uso-detalhe">
              Apenas filiais ativas (classificação V); a entidade de cobrança não entra na contagem.
            </span>
          </li>
          <li>
            <button
              type="button"
              className="empresa-config-link"
              onClick={() => abrirModuloEmNovaAba("/Empresas", "F")}
            >
              Fornecedores
            </button>
            {`: ${planoResumo.uso?.fornecedores ?? 0}`}
            {planoResumo.limites?.find((l) => l.codigo === "FORNECEDORES")?.valor_numerico != null &&
              ` / ${planoResumo.limites.find((l) => l.codigo === "FORNECEDORES").valor_numerico}`}
            <span className="empresa-config-uso-detalhe">
              Apenas fornecedores ativos (classificação F).
            </span>
          </li>
        </ul>
      </div>
      )}

      {(!standalone || possuiAssinaturaPendente || (mostrarConfirmacaoStandalone && planoPendente)) && (
      <div className="empresa-config-card empresa-config-plano-acoes">
        {!standalone && (
          <div className="empresa-config-hint">
            Pagamentos são processados pelo Asaas. Dados de cartão não são armazenados no Doc.
          </div>
        )}
        {possuiAssinaturaPendente && (
          <div className="empresa-config-hint empresa-config-hint--accent">
            Conclua o pagamento{" "}
            <button
              type="button"
              className="empresa-config-link"
              onClick={() => setShowFinanceiroModal(true)}
              disabled={aplicando}
            >
              AQUI
            </button>{" "}
            pendente para poder alterar o plano.
          </div>
        )}
        {!standalone && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <Button
              variant={planoPendente ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setShowPrecosModal(true)}
              disabled={aplicando || possuiAssinaturaPendente}
              title={possuiAssinaturaPendente ? "Conclua o pagamento pendente antes de alterar o plano." : undefined}
            >
              {botaoPlanoLabel}
            </Button>
          </div>
        )}
        {standalone && mostrarConfirmacaoStandalone && planoPendente && (
          <div style={{ marginTop: 8 }}>
            <Button
              variant="success"
              size="sm"
              onClick={handleConfirmarStandalone}
              disabled={aplicando || possuiAssinaturaPendente}
            >
              {aplicando ? <Spinner animation="border" size="sm" /> : "Confirmar plano"}
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Standalone (assistente): grid de planos sempre visível na tela, sem
          precisar abrir um modal separado — atende ao pedido de mostrar
          "onde constam todos os planos" diretamente na etapa. */}
      {standalone && !possuiAssinaturaPendente && (
        <div className="empresa-config-card" style={{ padding: 0, overflow: "hidden" }}>
          <Precos
            modoSelecao
            planoAtualCodigo={planoAtualCodigo}
            planoSelecionadoCodigo={planoPendente?.codigo}
            cicloInicial={planoPendente?.ciclo === "ANUAL" ? "anual" : "mensal"}
            onSelecionarPlano={handleSelecionarPlano}
            renderEntrePlanosELimites={
              planoPagoPendente(planoPendente) ? renderFormaPagamento : null
            }
          />
        </div>
      )}

      {!standalone && (
        <Modal
          show={showPrecosModal}
          onHide={() => setShowPrecosModal(false)}
          fullscreen
          scrollable
          backdrop="static"
        >
          <Modal.Body className="p-0">
            <Precos
              modoSelecao
              planoAtualCodigo={planoAtualCodigo}
              planoSelecionadoCodigo={planoPendente?.codigo}
              cicloInicial={planoPendente?.ciclo === "ANUAL" ? "anual" : "mensal"}
              onSelecionarPlano={handleSelecionarPlano}
              onFechar={() => setShowPrecosModal(false)}
            />
          </Modal.Body>
        </Modal>
      )}

      <EmpresasFinanceiroModal
        show={showFinanceiroModal}
        handleClose={() => setShowFinanceiroModal(false)}
        entidadeID={idEntidade}
        empresaNome={empresaNome || "Entidade"}
      />
    </>
  );
});

export default SelecaoPlano;
